import React, { useEffect, useState } from "react";
import axiosInstance from "../../services/axiosService";
import BuilderTable from "../../Layouts/BuilderTable";
import { toastError, toastSuccess } from "../../utils/toastUtils";
import Input from "../../components/FormComponents/Input";
import Button from "../../components/FormComponents/Button";
import SingleSelectBox from "../../components/FormComponents/SingleSelectBox";
import BuilderRightSideBar from "../../Layouts/BuilderRightSideBar";
import ButtonActionIcon from "../../components/BuilderComponents/ButtonActionIcon";
import Loader from "../../Layouts/Loader/Loader";
import CopyToClipboard from "../../components/BuilderComponents/CopyToClipboard";
import BuilderTabs from "../../Layouts/BuilderTabs";

const Monitor = () => {
  const [logs, setLogs] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);

  const fetchLogs = async (page = meta.page, limit = meta.limit, searchTerm = search) => {
    try {
      setLoading(true);
      setError(null); // Clear previous errors
      const { data } = await axiosInstance.get(
        `/proxy_logs?search=${encodeURIComponent(searchTerm)}&page=${page}&limit=${limit}`
      );

      const payload = data;
      const rows = payload?.data || [];
      const payloadMeta = payload?.meta || {};

      setLogs(rows);
      setMeta({
        page: payloadMeta.page || page,
        limit: payloadMeta.limit || limit,
        total: payloadMeta.total || 0,
        totalPages: payloadMeta.totalPages || 0
      });
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to fetch logs");
      setLogs([]); // Clear logs on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchLogs(1, meta.limit, search);
    }, 500);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const columns = [
    { key: "id", header: "ID" },
    { key: "method", header: "Method" },
    { key: "request_url", header: "Request URL" },
    { key: "response_status", header: "Status" },
    { key: "source_domain", header: "Source" },
    { key: "created_at", header: "Created At" },
    { key: "destination_domain", header: "Destination" }
  ];

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text ?? "");
      toastSuccess("Copied to clipboard");
    } catch {
      toastError("Copy failed");
    }
  };

  const customColumns = [
    {
      header: "Actions",
      render: (item) => (
        <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
          <ButtonActionIcon onClick={() => setSelectedLog(item)} type="open_in_new" />
          <ButtonActionIcon type="content_copy" onClick={() => copyToClipboard(item.request_url)} />
        </div>
      )
    }
  ];

  const tableData = logs.map((r) => ({
    ...r,
    created_at: r.created_at
      ? new Date(r.created_at).toLocaleString("en-IN", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true
        })
      : "-"
  }));

  const goToPage = (p) => {
    if (p < 1 || p > meta.totalPages) return;
    fetchLogs(p, meta.limit, search);
  };

  const limitOptions = [
    { id: "5", name: "5" },
    { id: "10", name: "10" },
    { id: "20", name: "20" }
  ];

  return (
    <div className="p_4 mx_auto h_100 overflow_scroll" style={{ maxWidth: "1200px" }}>
      <h1 className="mb_2">Proxy Logs</h1>

      {/* Search bar */}
      <div className="d_flex gap_2 mb_2 items_center justify_between">
        <div style={{ width: "400px" }}>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search.." />
        </div>
        <Button onClick={() => fetchLogs(1, meta.limit, search)} variant="secondary">
          Refresh
        </Button>
      </div>

      {loading && !error ? (
        <div style={{ height: "400px" }} className="d_flex items_center">
          <Loader />
        </div>
      ) : error ? (
        <div className="text_error m_2 ml_0">
          <strong>Error:</strong> {error}
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <BuilderTable
            columns={columns}
            data={tableData}
            customColumns={customColumns}
            noDataMessage={loading ? "Loading logs..." : "No logs found."}
            style={{
              minWidth: "1000px",
              borderCollapse: "collapse",
              textAlign: "left",
              tableLayout: "auto",
              wordWrap: "break-word"
            }}
          />
        </div>
      )}

      {/* Pagination */}
      <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center", justifyContent: "flex-end" }}>
        <Button onClick={() => goToPage(meta.page - 1)} disabled={meta.page <= 1} variant="secondary">
          Prev
        </Button>
        <span>
          Page {meta.page} of {meta.totalPages}
        </span>
        <div style={{ width: "100px" }}>
          <SingleSelectBox
            options={limitOptions}
            valueKey="id"
            labelKey={["name"]}
            selectedData={String(meta.limit)}
            onChange={(selectedOption) => {
              const newLimit =
                typeof selectedOption === "string" ? Number(selectedOption) : Number(selectedOption?.id ?? meta.limit);
              fetchLogs(1, newLimit, search);
            }}
            isSearch={false}
            placeholder="Limit"
          />
        </div>
        <Button onClick={() => goToPage(meta.page + 1)} disabled={meta.page >= meta.totalPages} variant="secondary">
          Next
        </Button>
      </div>

      {/* Details section */}
      <BuilderRightSideBar
        isOpen={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        heading={selectedLog ? `Log Details` : "Log Details"}
        size="small"
      >
        {selectedLog && (
          <div
            style={{
              // padding: 16,
              // borderRadius: 12,
              backgroundColor: "#f9fafb",
              border: "1px solid #e5e7eb",
              fontFamily: "Inter, sans-serif",
              fontSize: 14,
              color: "#111827",
              lineHeight: 1.5,
              height: "100%",
              overflowY: "auto"
            }}
          >
            {/* Copy Button */}
            {/* <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
              <Button onClick={() => copyToClipboard(JSON.stringify(selectedLog, null, 2))} variant="secondary">
                Copy JSON
              </Button>
            </div> */}

            <BuilderTabs
              tabs={[
                {
                  label: "Request",
                  content: (
                    <div className="p_2" style={{ marginTop: 8 }}>
                      <p>
                        <strong>URL:</strong> {selectedLog.request_url}
                      </p>
                      <p>
                        <strong>Method:</strong> {selectedLog.method}
                      </p>
                      <p>
                        <strong>Remote Addr:</strong> {selectedLog.remote_addr}
                      </p>

                      <PreWithCopy label="Query Params" value={selectedLog.query_params} />
                      <PreWithCopy label="Headers" value={selectedLog.request_headers} />
                      <PreWithCopy label="Body" value={selectedLog.request_body || "(empty)"} />
                    </div>
                  )
                },
                {
                  label: "Response",
                  content: (
                    <div className="p_2" style={{ marginTop: 8 }}>
                      <p>
                        <strong>Status:</strong> {selectedLog.response_status}
                      </p>
                      <p>
                        <strong>Latency (ms):</strong> {selectedLog.response_time_ms}
                      </p>

                      <PreWithCopy label="Headers" value={selectedLog.response_headers} />
                      <PreWithCopy label="Body" value={selectedLog.response_body || "(empty)"} />
                    </div>
                  )
                }
              ]}
              tabsClass="sidebar_tabs"
              containerClass=""
              contentClass=""
              contentStyle={{ marginTop: 8 }}
            />

            {/* Footer */}
            <div
              style={{
                marginTop: 16,
                padding: 8,
                borderTop: "1px solid #e5e7eb",
                color: "#4b5563",
                fontSize: 13
              }}
            >
              <strong>Created At:</strong> {selectedLog.created_at}
            </div>
          </div>
        )}
      </BuilderRightSideBar>
    </div>
  );
};

const PreWithCopy = ({ label, value }) => {
  const jsonString = typeof value === "string" ? value : JSON.stringify(value, null, 2);

  return (
    <div style={{ marginTop: 6, position: "relative" }}>
      <strong>{label}:</strong>
      <div style={{ position: "relative" }}>
        <PreStyle>{jsonString || "(empty)"}</PreStyle>
        <div style={{ position: "absolute", top: 8, right: 8 }}>
          <CopyToClipboard text={jsonString} />
        </div>
      </div>
    </div>
  );
};

const PreStyle = ({ children }) => (
  <pre
    style={{
      whiteSpace: "pre-wrap",
      backgroundColor: "#f3f4f6",
      padding: 8,
      borderRadius: 8,
      marginTop: 4,
      overflowX: "auto",
      fontSize: "12px",
      maxHeight: "300px"
    }}
  >
    {children}
  </pre>
);

export default Monitor;
